import { isEmpty, keyBy, uniqBy } from 'lodash';

import {
  ILoyaltyOfferConfigsQuery,
  IUserAttributes,
  IUserAttributesItem,
} from 'generated/sanity-graphql';
import {
  IIncentiveEvaluationResult,
  IncentiveEvaluationErrorCodes,
  IncentiveEvaluationMap,
} from 'state/loyalty/hooks/types';
import { ICartEntryType } from 'state/loyalty/in-restaurant-redemption';
import { ExternalOffer, LoyaltyOffer, RuleType, UserAttributesItem } from 'state/loyalty/types';
import { isUpcomingOffer } from 'state/loyalty/utils';
import { replaceKeyRegex } from 'utils/language';

import { IUserOfferAttributes, ReduceOffers } from './types';

const validateBatteryLevelRule = (attribute: IUserAttributesItem, value?: any) => {
  if (
    !attribute ||
    !attribute.useAttributeBatteryLevelFilter ||
    !attribute.useAttributeBatteryLevelValue
  ) {
    return true;
  }

  if (!value) {
    return false;
  }

  // Multiplying by 100 because the Capacitor Plugin informs the battery level in a range of 0 to 1
  // while the Sanity Configuration goes from 0 to 100.
  const userBatteryPercentage = Math.trunc(value * 100);

  switch (attribute.useAttributeBatteryLevelFilter) {
    case '>':
      if (userBatteryPercentage > attribute.useAttributeBatteryLevelValue) {
        return true;
      }
      break;
    case '<':
      if (userBatteryPercentage < attribute.useAttributeBatteryLevelValue) {
        return true;
      }
      break;
    default:
      break;
  }
  return false;
};

const validateBooleanAttribute = (attribute: IUserAttributesItem, value?: any) => {
  if (attribute.userAttributeBooleanValue === 'true') {
    return value === true;
  }
  return !value;
};

const validateStringAttribute = (attribute: IUserAttributesItem, value?: any) => {
  if (attribute.userAttributeStringFilter === '=') {
    return attribute.userAttributeStringValue === value;
  } else {
    return attribute.userAttributeStringValue !== value;
  }
};

const validateArrayAttribute = (attribute: IUserAttributesItem, value?: any) => {
  if (!value?.length) {
    return false;
  }
  if (attribute.userAttributeStringFilter === '=') {
    return value.includes(attribute.userAttributeStringValue);
  } else {
    return !value.includes(attribute.userAttributeStringValue);
  }
};

export const validUserAttributeRules = (
  offer: LoyaltyOffer,
  userAttributes: IUserOfferAttributes | undefined
) => {
  const validationMap = {
    [UserAttributesItem.BatteryLevel]: {
      fn: validateBatteryLevelRule,
      value: userAttributes?.userBatteryLevel,
    },
    [UserAttributesItem.DobDeleted]: {
      fn: validateBooleanAttribute,
      value: userAttributes?.dobDeleted,
    },
    [UserAttributesItem.PhoneNumberVerified]: {
      fn: validateBooleanAttribute,
      value: userAttributes?.phoneNumberVerified,
    },
    [UserAttributesItem.PromotionalEmails]: {
      fn: validateBooleanAttribute,
      value: userAttributes?.promotionalEmails,
    },
    [UserAttributesItem.ZipCode]: {
      fn: validateStringAttribute,
      value: userAttributes?.zipcode,
    },
    [UserAttributesItem.DeliveryAddresses]: {
      fn: validateBooleanAttribute,
      value: userAttributes?.hasDeliveryAddress,
    },
    [UserAttributesItem.FavoriteStores]: {
      fn: validateArrayAttribute,
      value: userAttributes?.favoriteStores,
    },
    [UserAttributesItem.FavoriteOffers]: {
      fn: validateArrayAttribute,
      value: userAttributes?.favoriteOffers,
    },
  };

  const offerUserAttributes = offer.rules?.find(
    rule => rule?.__typename === RuleType.UserAttributes
  );

  // If offer does not have user attributes rule do nothing
  if (!offerUserAttributes) {
    return true;
  }

  const { userAttributesItem: attributes } = offerUserAttributes as IUserAttributes;

  return attributes?.every(attribute => {
    if (!attribute?.attributeItem) {
      return false;
    }

    // Check sanity defined attribute to see if it matches with current user attribute value
    const { fn, value } = validationMap[attribute.attributeItem as UserAttributesItem];
    return fn(attribute, value);
  });
};

/**
 * Classify the offers that arrived from the CMS between two groups: those available to the current user and those that have already been applied to the cart.
 * Filter the offers that are not available and those that do not match the base offer models.
 */
export const reduceOffers: ReduceOffers = ({
  cmsOffers,
  personalizedOffers = [],
  baseOffers,
  appliedOffers,
  inRestaurantRedemptionCart,
  checkLoyaltyOfferAvailability,
  enableSortOffersForServiceMode,
  setOffersFeedbackMap,
  userOfferAttributes,
}) => {
  const appliedOffersMap = keyBy(appliedOffers, 'id');
  const baseOffersMap = keyBy(baseOffers, 'id');
  const personalizedOffersMap = keyBy(personalizedOffers, 'loyaltyEngineId');
  const inRestaurantRedemptionMap = keyBy(inRestaurantRedemptionCart, cartEntry =>
    cartEntry.type === ICartEntryType.OFFER ? cartEntry.details.offer._id : cartEntry.referenceId
  );

  let offers: LoyaltyOffer[] = [];
  const offersInCart: LoyaltyOffer[] = [];
  const bottomSortedOffers: LoyaltyOffer[] = [];
  const offerFeedback: IncentiveEvaluationMap = {};
  //Evaluating personalizedOffers first
  const mixedOffers = uniqBy([...personalizedOffers, ...cmsOffers], 'loyaltyEngineId');

  mixedOffers?.forEach(async (offer: LoyaltyOffer) => {
    if (
      !offer.loyaltyEngineId ||
      !offer._id ||
      !checkLoyaltyOfferAvailability(offer) ||
      !validUserAttributeRules(offer, userOfferAttributes)
    ) {
      return;
    }
    const personalizedOffer = personalizedOffersMap[offer.loyaltyEngineId];
    const engineOffer = baseOffersMap[offer.loyaltyEngineId];
    const metadata = personalizedOffer ? personalizedOffer?.metadata : engineOffer?.metadata;
    const fullOffer = {
      ...offer,
      // Offers are now considered stackable by default unless explicitly disabled.
      // We use the Earnings Calculations to determine how many offers each order can accept.
      isStackable: offer.isStackable !== false || engineOffer?.isStackable,
      metadata,
    };

    const offerIsUpcoming = isUpcomingOffer(offer);

    // Systemwide upcoming offer has a future startDate on Loyalty Between Date rule, therefore loyalty will not return the offer in
    // the personalizedOffers list, but we need to show the offer on the list if it's an upcoming offer
    // so adding it here.
    if (offerIsUpcoming && offer.__typename === 'SystemwideOffer') {
      offers.push(fullOffer);
    }

    if (appliedOffersMap[offer.loyaltyEngineId] || inRestaurantRedemptionMap[offer._id]) {
      offersInCart.push(fullOffer);
    } else if (personalizedOffer) {
      offers.push(fullOffer);
    } else if (engineOffer) {
      // If offer invalid for current service mode, add to bottom of list
      const offerHasInvalidServiceMode = engineOffer?.errors?.some(
        ({ code }) => code === IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
      );
      if (offerHasInvalidServiceMode && enableSortOffersForServiceMode) {
        bottomSortedOffers.push(fullOffer);
      } else {
        offers.push(fullOffer);
      }
    }

    if (engineOffer?.errors?.length) {
      offerFeedback[engineOffer.id] = engineOffer.errors as IIncentiveEvaluationResult[];
    }
  });

  if (bottomSortedOffers.length) {
    offers = offers.concat(bottomSortedOffers);
  }

  if (!isEmpty(offerFeedback)) {
    setOffersFeedbackMap(offerFeedback);
  }

  return [offers, offersInCart];
};

/**
 * @param loyaltyEngineId incentive Id that came in the url EG: /rewards/offers/{loyaltyEngineId}
 * @param offersInCart array of applied offers
 * @param offers array of available offers (without the applied)
 * @param selectFirstByDefault boolean
 * @returns
 * If the user access through /rewards/offers/{loyaltyEngineId}
 * returns the offer that matches with that ${loyaltyEngineId}. Undefined otherwise
 *
 * If the user access through /rewards/offers
 * returns undefined if ${selectFirstByDefault} is false.
 * returns the first applied incentive if there is any in ${offersInCart}, if not, returns the first offer in ${offers}.
 */
export const getSelectedIncentive = (
  loyaltyEngineId: string | undefined,
  offers: LoyaltyOffer[],
  offersInCart: LoyaltyOffer[],
  selectFirstByDefault: boolean,
  challengeIncentives?: ILoyaltyOfferConfigsQuery['allConfigOffer'] | undefined,
  externalOffers?: ExternalOffer[]
): LoyaltyOffer | ExternalOffer | undefined => {
  let selectedIncentive: LoyaltyOffer | ExternalOffer | undefined;
  if (loyaltyEngineId) {
    selectedIncentive = offersInCart?.find(offer => offer.loyaltyEngineId === loyaltyEngineId);
    const selectedChallengeIncentive = challengeIncentives?.find(
      incentive => incentive.loyaltyEngineId === loyaltyEngineId
    );
    return (
      selectedIncentive ??
      offers?.find(offer => offer.loyaltyEngineId === loyaltyEngineId) ??
      externalOffers?.find(offer => offer._id === loyaltyEngineId) ??
      selectedChallengeIncentive
    );
  }

  // first offer select by default doesn't apply
  if (selectFirstByDefault) {
    return undefined;
  }

  if (!offersInCart && !offers) {
    return undefined;
  }

  return offersInCart?.length ? offersInCart[0] : offers[0];
};

/**
 *
 * Given a string, will look for a key like {{KEY}} and will replace it with the given value.
 *
 * @param stringValue A string that might contain an specific key to be replaced
 * @param key the key you want to replaced with value
 * @param value the value you want to use to replace the KEY
 * @returns the string with the replaced key
 */
export const interpolateSanityKey = (stringValue: string, key: string, value: any) => {
  return stringValue.replace(replaceKeyRegex(key), value);
};

export const isGreyedOutOffer = (offer: LoyaltyOffer, isAuthenticated: boolean) => {
  if (isAuthenticated) {
    return false;
  }

  const requiresAutenticationRule = offer?.rules?.find(
    rule => rule?.__typename === RuleType.RequiresAuthentication
  );

  // Offers are closed by default. To be open they need the Authentication rule set to false explicitly
  if (!requiresAutenticationRule) {
    return true;
  }

  return (
    requiresAutenticationRule.__typename === RuleType.RequiresAuthentication &&
    Boolean(requiresAutenticationRule.requiresAuthentication)
  );
};
